import { Card, CardContent, LoadingSpinner, QueryErrorAlert } from '@shared/components';
import { isNotFoundError } from '@/services/api';
import { useThirdPartyInvoicing } from '../../hooks/useThirdPartyInvoicing';
import {
    THIRD_PARTY_INVOICING_FAILED,
    THIRD_PARTY_INVOICING_NOT_FOUND,
    THIRD_PARTY_MODE_NOTE,
    THIRD_PARTY_NOTHING_TO_INVOICE,
    THIRD_PARTY_NO_ADD_ONS,
    THIRD_PARTY_NO_SERVICES,
} from '../../utils/thirdPartyInvoicingCopy';
import { InvoiceSelectionLayout } from '../InvoiceSelectionLayout';
import { InvoiceSelectionSection } from '../InvoiceSelectionSection';
import { SoldAddOnsTable } from '../SoldAddOnsTable';
import { SoldServicesTable } from '../SoldServicesTable';
import { ThirdPartyInvoiceForm, ThirdPartyInvoiceNoticeAlert } from './ThirdPartyInvoiceForm';
import { ThirdPartyInvoicingDealerField } from './ThirdPartyInvoicingDealerField';

interface Props {
    vrm: string;
    reference: string;
    visit: string;
    availableBalance: string | null;
}

const NO_LINE_MESSAGES = new Map<string, string>();
const NOTE_CLASSES = 'text-sm text-mp-muted';

export function ThirdPartyInvoicingTab({ vrm, reference, visit, availableBalance }: Props) {
    const { query, lines, selection, tableRows, dealer, invoice } = useThirdPartyInvoicing(vrm, reference, visit);

    if (query.isLoading) {
        return <LoadingSpinner message="Loading what is left to invoice" className="p-4" />;
    }

    if (query.isError || !lines) {
        return (
            <div className="space-y-4">
                <ThirdPartyInvoiceNoticeAlert notice={invoice.notice} />
                <QueryErrorAlert
                    message={isNotFoundError(query.error) ? THIRD_PARTY_INVOICING_NOT_FOUND : THIRD_PARTY_INVOICING_FAILED}
                    onRetry={() => query.refetch()}
                    isRetrying={query.isFetching}
                />
            </div>
        );
    }

    if (lines.services.length === 0 && lines.addOns.length === 0) {
        return (
            <div className="space-y-4">
                <ThirdPartyInvoiceNoticeAlert notice={invoice.notice} />
                <p role="status" className={NOTE_CLASSES}>
                    {THIRD_PARTY_NOTHING_TO_INVOICE}
                </p>
            </div>
        );
    }

    return (
        <div className="@container space-y-4">
            <p className={NOTE_CLASSES}>{THIRD_PARTY_MODE_NOTE}</p>

            <InvoiceSelectionLayout
                panelLabel="Invoice selection"
                panel={
                    <Card>
                        <CardContent>
                            <InvoiceSelectionSection
                                basis="gross"
                                totals={selection.totals}
                                summaryLines={selection.summaryLines}
                                lineMessages={NO_LINE_MESSAGES}
                                onClear={selection.clear}
                                availableBalance={availableBalance}
                                outcome={null}
                                locked={false}
                            />
                            <div className="mt-4 space-y-4 border-t border-mp-border pt-4">
                                <ThirdPartyInvoicingDealerField dealer={dealer} />
                                <ThirdPartyInvoiceForm invoice={invoice} />
                            </div>
                        </CardContent>
                    </Card>
                }
                services={
                    <SoldServicesTable
                        basis="gross"
                        readOnlyItems
                        rows={tableRows.services}
                        emptyMessage={THIRD_PARTY_NO_SERVICES}
                        flexible={false}
                        selectedKeys={tableRows.selectedKeys}
                        onToggle={selection.toggle}
                    />
                }
                addOns={
                    <SoldAddOnsTable
                        basis="gross"
                        rows={tableRows.addOns}
                        emptyMessage={THIRD_PARTY_NO_ADD_ONS}
                        onToggle={selection.toggle}
                    />
                }
            />
        </div>
    );
}
